import * as React from "react";
import { Lock, Plus } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import * as formStyles from "../../../ui/components/Form/FormInput.style.css";
import { FeatureFlagValueInput } from "../system/FeatureFlagValueInput";
import * as featureFlagsApi from "../../lib/api/feature-flags";
import type { OrganizationFeatureFlagRow } from "../../lib/api/feature-flags";
import { useI18n } from "../../lib/i18n";

function defaultValueForFlag(flag: OrganizationFeatureFlagRow): string {
  if (flag.type === "boolean") return "true";
  if (flag.type === "enum") return flag.options?.[0] ?? "";
  return "";
}

export function OrganizationFeatureFlagsSection({
  organizationId,
  organizationName,
  token,
}: {
  organizationId: string;
  organizationName: string;
  token: string;
}) {
  const { t } = useI18n();
  const [flags, setFlags] = React.useState<OrganizationFeatureFlagRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [selectedFlagId, setSelectedFlagId] = React.useState("");
  const [draftValue, setDraftValue] = React.useState("");
  const [savingAdd, setSavingAdd] = React.useState(false);
  const [removingFlagId, setRemovingFlagId] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setFlags(await featureFlagsApi.fetchOrganizationFeatureFlags(token, organizationId));
  }, [token, organizationId]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    load()
      .catch((e) => {
        if (!cancelled) setError(e?.message || t("pro.organizations.featureOverrides.error.load"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load, t]);

  const visibleRows = flags.filter((f) => f.overrideValue !== null || !f.canEdit);
  const availableFlagsToAdd = flags.filter((f) => f.canEdit && f.overrideValue === null);
  const selectedFlag = flags.find((f) => f.id === selectedFlagId) ?? null;

  const cancelAdding = () => {
    setAdding(false);
    setSelectedFlagId("");
    setDraftValue("");
  };

  const handleAddOverride = async () => {
    if (!selectedFlagId) return;
    setSavingAdd(true);
    setError(null);
    try {
      await featureFlagsApi.upsertOrganizationFeatureFlagOverride(
        token,
        selectedFlagId,
        organizationId,
        draftValue,
      );
      cancelAdding();
      await load();
    } catch (e: any) {
      setError(e?.message || t("pro.system.featureFlags.error.addOverride"));
    } finally {
      setSavingAdd(false);
    }
  };

  const handleRemoveOverride = async (flagId: string) => {
    setRemovingFlagId(flagId);
    setError(null);
    try {
      await featureFlagsApi.deleteOrganizationFeatureFlagOverride(token, flagId, organizationId);
      await load();
    } catch (e: any) {
      setError(e?.message || t("pro.system.featureFlags.error.removeOverride"));
    } finally {
      setRemovingFlagId(null);
    }
  };

  if (loading) {
    return (
      <Text level="body-medium" style={{ color: "var(--color-fgSecondary)" }}>
        {t("shared.common.loading")}
      </Text>
    );
  }

  if (error && flags.length === 0) {
    return <Callout variant="error">{error}</Callout>;
  }

  const addButton = (
    <Button
      type="button"
      color="primary"
      variant="outline"
      iconLeft={<Plus size={14} />}
      onClick={() => {
        setError(null);
        setAdding(true);
      }}
    >
      {t("pro.organizations.featureOverrides.add")}
    </Button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {error && (
        <div style={{ marginBottom: 8 }}>
          <Callout variant="error">{error}</Callout>
        </div>
      )}

      {visibleRows.length > 0 && (
        <>
          {!adding && availableFlagsToAdd.length > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
              {addButton}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: adding ? 12 : 0 }}>
            {visibleRows.map((flag) => {
              const removing = removingFlagId === flag.id;
              const isOverride = flag.overrideValue !== null;
              return (
                <div
                  key={flag.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: "10px 0",
                    borderBottom: "1px solid var(--color-border)",
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Text level="body-medium" weight="bold">
                      {flag.label}
                    </Text>
                    <Text level="body-small" style={{ color: "var(--color-fgSecondary)" }}>
                      <span style={{ fontFamily: "monospace" }}>{flag.key}</span> ·{" "}
                      {isOverride
                        ? t("pro.organizations.featureOverrides.overrideValue")
                        : t("pro.organizations.featureOverrides.defaultValue")}{" "}
                      <span style={{ fontFamily: "monospace" }}>{flag.overrideValue ?? flag.value}</span>
                    </Text>
                  </div>
                  {flag.canEdit ? (
                    <Button
                      type="button"
                      color="neutral"
                      variant="outline"
                      size="sm"
                      onClick={() => void handleRemoveOverride(flag.id)}
                      disabled={removing}
                    >
                      {removing
                        ? t("pro.organizations.featureOverrides.removing")
                        : t("pro.organizations.featureOverrides.remove")}
                    </Button>
                  ) : (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 12,
                        color: "var(--color-fgMuted)",
                      }}
                    >
                      <Lock size={12} aria-hidden />
                      {t("pro.organizations.featureOverrides.managed")}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {adding ? (
        <div
          style={{
            borderTop: visibleRows.length > 0 ? "1px solid var(--color-border)" : "none",
            paddingTop: visibleRows.length > 0 ? 12 : 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <span className={formStyles.label}>{t("pro.users.addOverride")}</span>
          <select
            className={formStyles.input}
            value={selectedFlagId}
            onChange={(e) => {
              const flagId = e.target.value;
              setSelectedFlagId(flagId);
              const flag = flags.find((f) => f.id === flagId);
              setDraftValue(flag ? defaultValueForFlag(flag) : "");
            }}
            disabled={savingAdd}
            autoFocus
          >
            <option value="">{t("pro.organizations.featureOverrides.selectFlag")}</option>
            {availableFlagsToAdd.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label} ({f.key})
              </option>
            ))}
          </select>
          {selectedFlag && (
            <FeatureFlagValueInput
              type={selectedFlag.type}
              options={selectedFlag.options}
              value={draftValue}
              onChange={setDraftValue}
              disabled={savingAdd}
            />
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button type="button" color="neutral" variant="outline" size="sm" onClick={cancelAdding} disabled={savingAdd}>
              {t("shared.common.cancel")}
            </Button>
            <Button
              type="button"
              color="primary"
              size="sm"
              onClick={() => void handleAddOverride()}
              disabled={savingAdd || !selectedFlagId}
            >
              {savingAdd ? t("pro.system.featureFlags.saving") : t("pro.users.addOverride")}
            </Button>
          </div>
        </div>
      ) : (
        visibleRows.length === 0 && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "16px 0" }}>
            <Text level="body-small" style={{ color: "var(--color-fgMuted)" }}>
              {t("pro.organizations.featureOverrides.empty", { name: organizationName })}
            </Text>
            {availableFlagsToAdd.length > 0 && addButton}
          </div>
        )
      )}
    </div>
  );
}
